import { useEffect, useState } from "react";
import { Clock } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";

const HARI_WIB = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
const BULAN_WIB = [
  "Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember",
];
const BULAN_SINGKAT_WIB = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];

// WIB = UTC+7 sepanjang tahun (tidak ada DST). Hitung manual supaya tidak
// bergantung pada dukungan timezone/locale Intl di browser pengguna.
function formatWIB(base, singkat = false) {
  const wib = new Date(base.getTime() + (base.getTimezoneOffset() + 420) * 60000);
  const hh = String(wib.getHours()).padStart(2, "0");
  const mm = String(wib.getMinutes()).padStart(2, "0");
  const ss = String(wib.getSeconds()).padStart(2, "0");
  return `${HARI_WIB[wib.getDay()]}, ${wib.getDate()} ${(singkat ? BULAN_SINGKAT_WIB : BULAN_WIB)[wib.getMonth()]} ${wib.getFullYear()} · ${hh}:${mm}:${ss} WIB`;
}

export function WIBClock({ className = "" }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  // Selalu 1 baris; di layar < 400px pakai nama bulan singkat agar tidak terpotong.
  return (
    <div
      data-testid="wib-clock"
      className={`inline-flex max-w-full items-center gap-1.5 whitespace-nowrap rounded-md border border-brand-sky/60 bg-brand-mist px-2 py-1.5 text-[13px] leading-none font-semibold text-brand-deep shadow-sm ${className}`}
    >
      <Clock className="w-3.5 h-3.5 text-brand-teal shrink-0" />
      <span className="tabular-nums min-[400px]:hidden">{formatWIB(now, true)}</span>
      <span className="tabular-nums hidden min-[400px]:inline">{formatWIB(now)}</span>
    </div>
  );
}

export function PageHeader({ title, subtitle, children, icon: Icon }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
      <div className="flex items-start gap-3">
        {Icon && (
          <div className="w-11 h-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5" />
          </div>
        )}
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 font-display">{title}</h1>
          {subtitle && <p className="text-sm text-slate-500 mt-1 max-w-2xl">{subtitle}</p>}
        </div>
      </div>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}

export function SimpleSelect({ value, onChange, options, placeholder, testId, className, labelKey = "label", valueKey = "value" }) {
  return (
    <Select value={value != null ? String(value) : undefined} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className={`bg-white ${className || "w-[180px]"}`}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => {
          const val = typeof o === "object" ? String(o[valueKey]) : String(o);
          const lbl = typeof o === "object" ? o[labelKey] : o;
          return (
            <SelectItem key={val} value={val} data-testid={`${testId}-opt-${val}`}>
              {lbl}
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

export function FilterCard({ children }) {
  return (
    <Card className="mb-6 border-slate-200 shadow-sm">
      <CardContent className="p-4 flex flex-wrap items-end gap-3">{children}</CardContent>
    </Card>
  );
}

export function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
      {children}
    </div>
  );
}

export function EmptyState({ title, desc, icon: Icon }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="empty-state">
      {Icon && <Icon className="w-12 h-12 text-slate-300 mb-3" />}
      <h3 className="text-lg font-semibold text-slate-700">{title}</h3>
      {desc && <p className="text-sm text-slate-500 mt-1 max-w-md">{desc}</p>}
    </div>
  );
}

export function KpiCard({ icon: Icon, label, value, tone = "slate", sub }) {
  const tones = {
    slate: "bg-primary text-primary-foreground",
    blue: "bg-brand-sky text-brand-deep",
    emerald: "bg-emerald-600 text-white",
    amber: "bg-amber-500 text-white",
    rose: "bg-rose-600 text-white",
  };
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="p-5 flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${tones[tone]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
          <div className="text-2xl font-extrabold text-slate-900 tabular-nums">{value}</div>
          {sub && <div className="text-xs text-slate-400">{sub}</div>}
        </div>
      </CardContent>
    </Card>
  );
}
